import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* eslint-disable no-underscore-dangle */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { uniqBy } from 'lodash';
import entityComposition from '../../common/classes/entityComposition';
import { labelSelector, valueSelector, enabledSelector, newPathSelector } from '../selectors';
import localChangeSelector from '../selectors/localChangeSelector';
import { UncomposedSinglePickerField as SinglePickerField } from './singlePickerField';
import OptionMultiPicker from './optionMultiPicker';
const writeMode = {
  REFERENCE: 'REFERENCE',
  DATA: 'DATA'
};
const toReference = actionValue => ({
  [actionValue.identifier]: {
    _formRef: actionValue.identifier
  }
});
const toData = actionValue => ({
  [actionValue.identifier]: {
    title: actionValue.title
  }
});
const toReferenceMulti = actionValue => {
  if (!actionValue) {
    return null;
  }
  const referenceValues = {};
  const actionValueKeys = Object.keys(actionValue);
  actionValueKeys.forEach(avKey => {
    referenceValues[actionValue[avKey].identifier || actionValue[avKey].id] = {
      _formRef: actionValue[avKey].identifier || actionValue[avKey].id
    };
  });
  return referenceValues;
};
const toDataMulti = actionValue => {
  if (!actionValue) {
    return null;
  }
  const dataValues = {};
  const actionValueKeys = Object.keys(actionValue);
  actionValueKeys.forEach(avKey => {
    dataValues[actionValue[avKey].identifier || actionValue[avKey].id || avKey] = {
      title: actionValue[avKey].title
    };
  });
  return dataValues;
};
const toIdentifiableValue = function toIdentifiableValue(mode, value) {
  let active = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  let allowsMultipleSelection = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  if (allowsMultipleSelection) {
    const keys = Object.keys(active);
    const activeValue = {};
    keys.forEach(key => {
      if (mode === writeMode.DATA && value) {
        if (!value[key]) {
          return null;
        }
        activeValue[key] = {
          identifier: key,
          title: value[key].title
        };
      } else {
        if (!active[key]) {
          return null;
        }
        activeValue[key] = _objectSpread(_objectSpread({}, active[key]), {}, {
          identifier: active[key].id
        });
      }
      return null;
    });
    return activeValue;
  }
  if (mode === writeMode.DATA && value) {
    const _Object$keys = Object.keys(value),
      _Object$keys2 = _slicedToArray(_Object$keys, 1),
      key = _Object$keys2[0];
    return {
      identifier: key,
      title: value[key].title
    };
  }
  if (!active.id) {
    return null;
  }
  return _objectSpread(_objectSpread({}, active), {}, {
    identifier: active.id
  });
};

/**
 * Look at the text filter that was applied and try to
 * identify the index that was matched during search.
 */
const toArrayOptions = function toArrayOptions(filters, options, activeValue) {
  let allowsMultipleSelection = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  const addSelectedValue = function addSelectedValue() {
    let opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    if (allowsMultipleSelection && activeValue) {
      const keys = Object.keys(activeValue);
      keys.forEach(key => {
        if (activeValue[key] && activeValue[key].identifier && !opts.some(opt => opt.identifier === activeValue[key].identifier)) {
          // If the shown option isn't in the list, put it there (at the first position)
          opts.unshift(activeValue[key]);
        }
      });
    } else if (activeValue && !opts.some(opt => opt.identifier === activeValue.identifier)) {
      // If the shown option isn't in the list, put it there (at the first position)
      opts.unshift(activeValue);
    }
    return opts;
  };
  const _filters$text = filters.text,
    textIndexes = _filters$text === void 0 ? [] : _filters$text;
  const _textIndexes = _slicedToArray(textIndexes, 1),
    firstIndex = _textIndexes[0];
  const validIndexes = textIndexes.map(_ref => {
    let index = _ref.index;
    return index;
  });
  if (!firstIndex) {
    return addSelectedValue();
  }
  const getIndex = function getIndex() {
    let opt = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return validIndexes.find(key => {
      const option = opt[key];
      // See if the text search matches the _value_ of the index
      return option === null || option === void 0 ? void 0 : option.toLowerCase().includes(firstIndex.value.toLowerCase());
    });
  };
  const nonUniqueOpts = (options || []).map(option => {
    const index = getIndex(option);
    if (!index) {
      return null;
    }
    return _objectSpread(_objectSpread({}, option), {}, {
      title: option[index],
      identifier: option.id
    });
    // Filter out any options that don't match the current filter
    // (null case)
  }).filter(opt => !!opt);
  const opts = uniqBy(nonUniqueOpts, 'title');
  addSelectedValue(opts);
  return opts;
};
const FormSearchField = _ref2 => {
  let _ref2$allowsMultipleS = _ref2.allowsMultipleSelection,
    allowsMultipleSelection = _ref2$allowsMultipleS === void 0 ? false : _ref2$allowsMultipleS,
    _ref2$allowsOther = _ref2.allowsOther,
    allowsOther = _ref2$allowsOther === void 0 ? false : _ref2$allowsOther,
    _ref2$contentPath = _ref2.contentPath,
    contentPath = _ref2$contentPath === void 0 ? undefined : _ref2$contentPath,
    deferred = _ref2.deferred,
    deferredActions = _ref2.deferredActions,
    enabled = _ref2.enabled,
    format = _ref2.format,
    onChange = _ref2.onChange,
    path = _ref2.path,
    _ref2$pickerType = _ref2.pickerType,
    pickerType = _ref2$pickerType === void 0 ? undefined : _ref2$pickerType,
    _ref2$value = _ref2.value,
    value = _ref2$value === void 0 ? null : _ref2$value,
    _ref2$values = _ref2.values,
    values = _ref2$values === void 0 ? null : _ref2$values;
  const serverShow = deferredActions.serverShow,
    serverIndex = deferredActions.serverIndex;
  const localChangeFunc = localChangeSelector({}, {
    allowsMultipleSelection,
    allowsOther,
    contentPath,
    deferred,
    deferredActions,
    enabled,
    format,
    onChange,
    path,
    pickerType,
    value,
    values
  });
  const mode = pickerType === 'formSearchField' ? writeMode.REFERENCE : writeMode.DATA;
  const toValue = mode === writeMode.REFERENCE ? toReference : toData;
  const toValues = mode === writeMode.REFERENCE ? toReferenceMulti : toDataMulti;
  let active;
  let loading;
  if (allowsMultipleSelection && value && Object.keys(value).length > 0) {
    const valueKeys = Object.keys(value);
    const newActive = {};
    valueKeys.forEach(key => {
      var _deferred, _deferred$value$key$_;
      newActive[value[key]._formRef || key] = ((_deferred = deferred[value[key]._formRef || key]) === null || _deferred === void 0 ? void 0 : _deferred.active) || value[key];
      loading = ((_deferred$value$key$_ = deferred[value[key]._formRef]) === null || _deferred$value$key$_ === void 0 ? void 0 : _deferred$value$key$_.loading) || false;
    });
    active = newActive;
  } else if (!allowsMultipleSelection) {
    var _deferred$id, _deferred$id2;
    let id = value === null || value === void 0 ? void 0 : value.id;
    if (value && Object.keys(value).length > 0) {
      id = value[Object.keys(value)[0]]._formRef;
    }
    active = (deferred === null || deferred === void 0 || (_deferred$id = deferred[id]) === null || _deferred$id === void 0 ? void 0 : _deferred$id.active) || {};
    loading = (deferred === null || deferred === void 0 || (_deferred$id2 = deferred[id]) === null || _deferred$id2 === void 0 ? void 0 : _deferred$id2.loading) || false;
  }
  const _deferred$path = deferred[path],
    _deferred$path2 = _deferred$path === void 0 ? {} : _deferred$path,
    indexQuery = _deferred$path2.indexQuery,
    _deferred$path2$index = _deferred$path2.indexQuery,
    _deferred$path2$index2 = _deferred$path2$index === void 0 ? {} : _deferred$path2$index,
    _deferred$path2$index3 = _deferred$path2$index2.filters,
    filters = _deferred$path2$index3 === void 0 ? {} : _deferred$path2$index3,
    _deferred$path2$hasNe = _deferred$path2.hasNext,
    hasNext = _deferred$path2$hasNe === void 0 ? false : _deferred$path2$hasNe,
    _deferred$path2$optio = _deferred$path2.options,
    options = _deferred$path2$optio === void 0 ? [] : _deferred$path2$optio;
  const displayValue = actionValue => {
    if (!actionValue) {
      return null;
    }
    if (allowsMultipleSelection) {
      return Object.values(actionValue || {}).map(av => av.title).join(', ');
    }
    return actionValue === null || actionValue === void 0 ? void 0 : actionValue.title;
  };
  const wrappedOnChange = action => {
    const actionValue = action.value;
    if (!actionValue) {
      return onChange(localChangeFunc(_objectSpread(_objectSpread({}, action), {}, {
        path,
        displayValue: displayValue(actionValue)
      })));
    }
    return onChange(localChangeFunc(_objectSpread(_objectSpread({}, action), {}, {
      value: allowsMultipleSelection ? toValues(actionValue) : toValue(actionValue),
      path,
      displayValue: displayValue(actionValue)
    })));
  };
  useEffect(() => {
    if (mode === writeMode.DATA) {
      return;
    }

    // value has been cleared
    if (!value) {
      return;
    }
    if (allowsMultipleSelection) {
      const keys = Object.keys(value); // value is in the form of { id: { _formRef: id } }
      keys.forEach(key => {
        var _active$key;
        // Avoid reloading the same record.
        // However, when lots of formSearchFields are on the same page
        // loading appears to be done simultaneously, so this check doesn't always work and an
        // additional check is necessary in serverShow
        if (key && active && !((_active$key = active[key]) !== null && _active$key !== void 0 && _active$key.title) && !loading) {
          serverShow(key, {
            // was used in the reducer to set the active value keyed by path, but now just uses id
            path,
            contentPaths: ['title'] // used by the server to limit the fields returned
          });
        }
      });
    } else {
      const _Object$keys3 = Object.keys(value),
        _Object$keys4 = _slicedToArray(_Object$keys3, 1),
        key = _Object$keys4[0]; // value is in the form of { id: { _formRef: id } }

      // Avoid reloading the same record.
      // However, when lots of formSearchFields are on the same page
      // loading appears to be done simultaneously, so this check doesn't always work and an
      // additional check is necessary in serverShow
      // Call when we have a value ref but haven't loaded the record yet
      // (active.title === undefined).
      // Once loaded, active.title is set (string or null); do not call again to avoid reloading.
      if (key && active.title === undefined && !loading) {
        serverShow(key, {
          // was used in the reducer to set the active value keyed by path, but now just uses id
          path,
          contentPaths: ['title'] // used by the server to limit the fields returned
        });
      }
    }
  }, [loading, mode, path, serverShow, value, active, allowsMultipleSelection]);
  const onTextChange = text => {
    const formTypeIdentifiers = format.formTypeIdentifiers,
      _format$indexes = format.indexes,
      indexes = _format$indexes === void 0 ? ['title'] : _format$indexes;
    const filterIndexes = indexes.map(index => ({
      index,
      value: text
    }));
    if (!text || text.length === 0) {
      return;
    }
    serverIndex(formTypeIdentifiers, path, 'deferred', _objectSpread(_objectSpread({}, indexQuery), {}, {
      filters: _objectSpread({
        text: filterIndexes
      }, format.filters)
    }));
  };
  const activeValue = toIdentifiableValue(mode, value, active, allowsMultipleSelection);
  if (allowsMultipleSelection) {
    return /*#__PURE__*/React.createElement(OptionMultiPicker, {
      allowsMultipleSelection: allowsMultipleSelection,
      allowsOther: allowsOther,
      allowCreate: allowsOther,
      onChange: wrappedOnChange,
      options: toArrayOptions(filters, options, activeValue, allowsMultipleSelection),
      value: activeValue,
      values: values,
      onTextChange: onTextChange,
      dropdownIndicatorGlyph: "magnifying-glass",
      placeholder: "Start typing to search...",
      endMessage: hasNext ? 'There are more results, continue to type to filter...' : null,
      isLoading: loading || false,
      isSearching: loading || false,
      enabled: enabled,
      path: path,
      labelKey: "title",
      valueKey: "identifier",
      verticalPadding: false
    });
  }
  return /*#__PURE__*/React.createElement(SinglePickerField, {
    format: format,
    allowsOther: allowsOther,
    onChange: wrappedOnChange,
    options: toArrayOptions(filters, options, activeValue, allowsMultipleSelection),
    value: activeValue,
    onTextChange: onTextChange,
    dropdownIndicatorGlyph: "magnifying-glass",
    placeholder: "Start typing to search...",
    endMessage: hasNext ? 'There are more results, continue to type to filter...' : null,
    isLoading: loading || false,
    isSearching: loading || false,
    enabled: enabled,
    path: path
  });
};
FormSearchField.propTypes = {
  allowsMultipleSelection: PropTypes.bool,
  allowsOther: PropTypes.bool,
  contentPath: PropTypes.string,
  format: PropTypes.shape({
    label: PropTypes.string,
    indexes: PropTypes.arrayOf(PropTypes.string),
    filters: PropTypes.shape({}),
    formTypeIdentifiers: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  deferred: PropTypes.shape({}).isRequired,
  deferredActions: PropTypes.shape({
    serverIndex: PropTypes.func,
    serverShow: PropTypes.func,
    serverFilter: PropTypes.func
  }).isRequired,
  indexes: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  values: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.string)]),
  pickerType: PropTypes.oneOf(['formSearchField', 'optionSearchField']),
  path: PropTypes.string.isRequired,
  enabled: PropTypes.bool.isRequired
};
export { FormSearchField as UncomposedFormSearchField };
export default entityComposition(FormSearchField, ['deferred'], {
  enabled: enabledSelector,
  label: labelSelector,
  path: newPathSelector,
  value: valueSelector
});